/** Product-visual field-assessment vocabulary shared by persistence and render gating. */

import type { ProductVisualCell } from "./product-visuals"

export const ETIQUETTE_FIELD = {
  name: "name",
  flavor: "flavor",
  ingredients: "ingredients",
  dosage: "dosage",
  directionsForUse: "directions_for_use",
} as const

export type EtiquetteField = (typeof ETIQUETTE_FIELD)[keyof typeof ETIQUETTE_FIELD]

/** Every persisted etiquette assessment, in the stable order used by content
 *  snapshot hashing. This belongs to field assessment, not the retired Studio
 *  quality report. */
export const ETIQUETTE_ASSESSMENT_FIELDS = [
  ETIQUETTE_FIELD.name,
  ETIQUETTE_FIELD.flavor,
  ETIQUETTE_FIELD.ingredients,
  ETIQUETTE_FIELD.dosage,
  ETIQUETTE_FIELD.directionsForUse,
] as const satisfies readonly EtiquetteField[]

export const FIELD_QUALITY_STATUS = {
  accepted: "accepted",
  missing: "missing",
  invalid: "invalid",
  uncertain: "uncertain",
  judgeUnavailable: "judge_unavailable",
  /** Legacy status from #218's directions short-circuit. #223 removed the
   *  short-circuit, so nothing produces `excluded` any more; the value and its
   *  DB CHECK stay only so pre-re-backfill rows still read. Drop both once the
   *  fleet has re-assessed (needs a schema migration). */
  excluded: "excluded",
} as const

export type FieldQualityStatus = (typeof FIELD_QUALITY_STATUS)[keyof typeof FIELD_QUALITY_STATUS]

export type FieldQualityDeterministicResult = "accepted" | "missing" | "suspicious" | "excluded"

export type FieldQualityJudgeOutcome = "accepted" | "invalid" | "uncertain" | "unavailable"

// Batch-render browse grid (issues #220/#371). One live image (current or
// outdated) plus its immutable publication-time provenance snapshot. Null
// means the artifact predates snapshotting; callers must render Unknown.
export type ProductVisualStudioCard = {
  simpleSku: string
  productTitle: string
  configSku: string
  cell: ProductVisualCell
}

export type ProductVisualBrowseImage = ProductVisualStudioCard

export type ProductVisualImagesResponse = {
  page: number
  pageSize: number
  total: number
  counts: ProductVisualStudioCounts
  items: ProductVisualBrowseImage[]
}

export const PRODUCT_VISUAL_STUDIO_STATUS = {
  images: "images",
  ready: "ready",
  gaps: "gaps",
  excluded: "excluded",
} as const

export type ProductVisualStudioStatus =
  (typeof PRODUCT_VISUAL_STUDIO_STATUS)[keyof typeof PRODUCT_VISUAL_STUDIO_STATUS]

export type ProductVisualStudioCounts = {
  images: number
  ready: number
  gaps: number
  excluded: number
}

export type ProductVisualWorklistItem = {
  status: Exclude<ProductVisualStudioStatus, "images">
} & ProductVisualStudioCard

export type ProductVisualWorklistResponse = {
  page: number
  pageSize: number
  counts: ProductVisualStudioCounts
  items: ProductVisualWorklistItem[]
}
